import { DropboxFileItem, DropboxFileListLoadedPathsParams, DropboxFileListResponse } from '@common/types/dropbox';
import { api } from '@services/api';

type GetMediaAssetsDropboxFilesArgs = {
    path: string;
    page?: number;
    page_size?: number;
    ordering?: string;
};

type ToggleMediaAssetsFavoriteArgs = {
    path: string;
    folderPath: string;
};

const mediaAssetsDropboxApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getMediaAssetsDropboxFiles: builder.query<
            DropboxFileListResponse<'media_assets'>,
            GetMediaAssetsDropboxFilesArgs
        >({
            query: (params) => ({
                url: `dropbox/files/`,
                params: {
                    ...params,
                    type: 'media_assets',
                },
            }),
            providesTags: (_res, _err, arg) => [
                {
                    type: 'MediaAssetsDropboxFiles',
                    id: `PATH:${arg.path}`,
                },
                {
                    type: 'MediaAssetsDropboxFiles',
                    id: `${arg.path}-${arg.page ?? 1}-${arg.page_size ?? ''}-${arg.ordering ?? ''}`,
                },
            ],
        }),
        getMediaAssetsDropboxFilesLoadedPaths: builder.query<
            {
                path: string;
                type: 'media_assets';
                results: DropboxFileItem[];
                /**
                 * Remaining count of items that can be loaded for the given path.
                 * This is useful for determining if there are more items to load, in a infinite scroll scenario.
                 * If the count is 0, it means there are no more items to load for the given path.
                 */
                count: number;
            },
            DropboxFileListLoadedPathsParams<'media_assets'>
        >({
            query: (params) => ({
                url: `dropbox/files/`,
                method: 'POST',
                body: {
                    ...params,
                    type: 'media_assets',
                },
            }),
            providesTags: (_res, _err, arg) => [
                {
                    type: 'MediaAssetsDropboxFiles',
                    id: `PATH:${arg.path ?? ''}-LOADED_PATHS:${arg.loaded_paths?.join(',') ?? ''}-${
                        arg.page_size ?? ''
                    }-${arg.ordering ?? ''}`,
                },
                {
                    type: 'MediaAssetsDropboxFiles',
                    id: `PATH:${arg.path ?? ''}`,
                },
            ],
        }),

        getMediaAssetsDropboxDownloadJson: builder.query<
            { type: 'media_assets' } & Pick<DropboxFileItem, 'path' | 'download_url' | 'stream_url' | 'url_expires_at'>,
            string
        >({
            query: (path) => ({
                url: `dropbox/download/`,
                params: {
                    path,
                    type: 'media_assets',
                    response_type: 'json',
                },
            }),
        }),
        mediaAssetsFavorite: builder.mutation<void, ToggleMediaAssetsFavoriteArgs>({
            query: ({ path }) => ({
                url: `dropbox/favorites/`,
                method: 'POST',
                body: { path, type: 'media_assets' },
            }),
            invalidatesTags: (_res, _err, { path, folderPath }) => [
                {
                    type: 'MediaAssetsDropboxFiles',
                    id: `PATH:${folderPath ?? ''}`,
                },
            ],
        }),
        mediaAssetsUnfavorite: builder.mutation<void, ToggleMediaAssetsFavoriteArgs>({
            query: ({ path }) => ({
                url: `dropbox/favorites/`,
                params: { path, type: 'media_assets' },
                method: 'DELETE',
            }),
            invalidatesTags: (_res, _err, { path, folderPath }) => [
                {
                    type: 'MediaAssetsDropboxFiles',
                    id: `PATH:${folderPath ?? ''}`,
                },
            ],
        }),
    }),
});

export const {
    useGetMediaAssetsDropboxFilesQuery,
    useMediaAssetsFavoriteMutation,
    useMediaAssetsUnfavoriteMutation,
    useLazyGetMediaAssetsDropboxFilesLoadedPathsQuery,
    useLazyGetMediaAssetsDropboxFilesQuery,
    useLazyGetMediaAssetsDropboxDownloadJsonQuery,
} = mediaAssetsDropboxApi;
export default mediaAssetsDropboxApi;
